/**
 * HowItWorksFlow - Reusable component explaining the complete loan flow
 * Shows 4 steps: Fund Vault → Issue LOC → Apply for Loan → Repay or Bank Redeems
 */

import { Link } from 'react-router-dom'
import { Card, CardContent } from './Card'
import { CheckCircleIcon } from '../../assets/icons'

export interface StepProgress {
  fundVault: boolean // Step 1: Has deposited collateral
  issueLOC: boolean // Step 2: Has issued at least one LOC
  applyLoan: boolean // Step 3: Has submitted loan application
  loanStatus?: 'pending' | 'approved' | 'disbursed' | 'repaid' // Step 4 status
}

interface HowItWorksFlowProps {
  className?: string
  variant?: 'full' | 'compact'
  progress?: StepProgress // Optional progress tracking
  showProgress?: boolean // Whether to show progress indicators
  clickable?: boolean // Whether cards should be clickable/navigable
}

export function HowItWorksFlow({ 
  className = '', 
  variant = 'full',
  progress,
  showProgress = false,
  clickable = false 
}: HowItWorksFlowProps) {
  
  // Route mapping for each step
  const stepRoutes: Record<string, string> = {
    'Fund Vault': '/vaults',
    'Issue LOC': '/locs',
    'Apply for Loan': '/loans',
    'Repay or Redeem': '/loans', // Also loans page for status
  }
  
  // Helper to get status icon for each step
  const getStepStatusIcon = (stepKey: string) => {
    if (!showProgress || !progress) return null
    
    switch (stepKey) {
      case 'Fund Vault':
        return progress.fundVault ? (
          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
        ) : null
      
      case 'Issue LOC':
        return progress.issueLOC ? (
          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
        ) : null
      
      case 'Apply for Loan':
        return progress.applyLoan ? (
          <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
        ) : null
      
      case 'Repay or Redeem':
        if (!progress.loanStatus) return null
        
        // Pending loan
        if (progress.loanStatus === 'pending' || progress.loanStatus === 'approved') {
          return (
            <svg className="w-6 h-6 text-orange-600 dark:text-orange-400 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )
        }
        
        // Disbursed or repaid
        if (progress.loanStatus === 'disbursed' || progress.loanStatus === 'repaid') {
          return <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
        }
        
        return null
      
      default:
        return null
    }
  }
  const steps = [
    {
      number: 1,
      title: 'Fund Your Vault',
      description: variant === 'full' 
        ? 'Deposit crypto assets (BTC, WETH, USDC) to your secure vault. You retain full custody and control of your funds.'
        : 'Deposit crypto to your vault. Keep custody.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
      color: 'blue',
      key: 'Fund Vault',
    },
    {
      number: 2,
      title: 'Issue Letter of Credit',
      description: variant === 'full'
        ? 'Create a payment guarantee (LOC) backed by your collateral. Designate a beneficiary (bank or lender) who can redeem it.'
        : 'Issue LOC to bank/lender. They can redeem if you default.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'green',
      key: 'Issue LOC',
    },
    {
      number: 3,
      title: 'Apply for Fiat Loan',
      description: variant === 'full'
        ? 'Present your LOC to partner banks. Complete KYC, share your IBAN, and receive fiat currency (USD, EUR) to your bank account.'
        : 'Complete KYC, share IBAN, get fiat loan.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      color: 'orange',
      key: 'Apply for Loan',
    },
    {
      number: 4,
      title: 'Repay or Bank Redeems',
      description: variant === 'full'
        ? 'Make payments according to loan terms and reclaim your collateral. If you default, the bank redeems your LOC to recover the loan.'
        : 'Repay loan → Get collateral back. Default → Bank redeems LOC.',
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      color: 'purple',
      key: 'Repay or Redeem',
    },
  ]

  const colorClasses = {
    blue: {
      bg: 'bg-blue-100 dark:bg-blue-900/30',
      text: 'text-blue-600 dark:text-blue-400',
      border: 'border-blue-300 dark:border-blue-700',
      icon: 'text-blue-600 dark:text-blue-400',
    },
    green: {
      bg: 'bg-green-100 dark:bg-green-900/30',
      text: 'text-green-600 dark:text-green-400',
      border: 'border-green-300 dark:border-green-700',
      icon: 'text-green-600 dark:text-green-400',
    },
    orange: {
      bg: 'bg-orange-100 dark:bg-orange-900/30',
      text: 'text-orange-600 dark:text-orange-400',
      border: 'border-orange-300 dark:border-orange-700',
      icon: 'text-orange-600 dark:text-orange-400',
    },
    purple: {
      bg: 'bg-purple-100 dark:bg-purple-900/30',
      text: 'text-purple-600 dark:text-purple-400',
      border: 'border-purple-300 dark:border-purple-700',
      icon: 'text-purple-600 dark:text-purple-400',
    },
  }

  if (variant === 'compact') {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 ${className}`}>
        {steps.map((step, index) => {
          const colors = colorClasses[step.color as keyof typeof colorClasses]
          const route = stepRoutes[step.key]
          const cardClassName = `relative p-4 rounded-lg border-2 ${colors.border} ${colors.bg} ${
            clickable ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 group' : ''
          }`
          
          const cardContent = (
            <>
              {/* Step Number Badge */}
              <div className={`absolute -top-3 -left-3 w-8 h-8 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center`}>
                <span className={`text-sm font-bold ${colors.text}`}>{step.number}</span>
              </div>

              {/* Status Icon (bottom-left) */}
              {showProgress && getStepStatusIcon(step.key) && (
                <div className="absolute -bottom-2 -left-2 bg-white rounded-full p-1 shadow-lg">
                  {getStepStatusIcon(step.key)}
                </div>
              )}

              {/* Icon */}
              <div className={`${colors.icon} mb-2`}>
                {step.icon}
              </div>

              {/* Content */}
              <h4 className={`text-sm font-bold ${colors.text} mb-1 ${clickable ? 'group-hover:underline' : ''}`}>
                {step.title}
              </h4>
              <p className="text-xs text-gray-700 dark:text-gray-300">{step.description}</p>

              {/* Arrow (not on last item) */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute -right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-600">
                  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              )}
            </>
          )
          
          return clickable && route ? (
            <Link key={step.key} to={route} className={cardClassName}>
              {cardContent}
            </Link>
          ) : (
            <div key={step.key} className={cardClassName}>
              {cardContent}
            </div>
          )
        })}
      </div>
    )
  }

  // Full variant
  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="mb-6">
          <h3 className="text-2xl font-display font-black uppercase text-text-primary mb-2">
            How It Works
          </h3>
          <p className="text-text-secondary">
            From crypto collateral to fiat loans - here's your complete journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {steps.map((step, index) => {
            const colors = colorClasses[step.color as keyof typeof colorClasses]
            const route = stepRoutes[step.key]
            const cardClassName = `relative p-6 rounded-xl border-2 ${colors.border} ${colors.bg} ${
              clickable ? 'cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all duration-200 group' : ''
            }`
            
            const cardContent = (
              <>
                {/* Step Number Badge */}
                <div className={`absolute -top-4 -left-4 w-12 h-12 rounded-full ${colors.bg} border-4 ${colors.border} flex items-center justify-center shadow-lg`}>
                  <span className={`text-lg font-bold ${colors.text}`}>{step.number}</span>
                </div>

                {/* Status Icon (bottom-left) */}
                {showProgress && getStepStatusIcon(step.key) && (
                  <div className="absolute -bottom-3 -left-3 bg-white dark:bg-gray-800 rounded-full p-1.5 shadow-lg border-2 border-gray-200 dark:border-gray-700">
                    {getStepStatusIcon(step.key)}
                  </div>
                )}

                {/* Icon and Title */}
                <div className="flex items-start gap-4 mb-3">
                  <div className={`${colors.icon} flex-shrink-0 mt-1`}>
                    {step.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className={`text-lg font-bold ${colors.text} mb-2 ${clickable ? 'group-hover:underline' : ''}`}>
                      {step.title}
                    </h4>
                    <p className="text-sm text-gray-700 dark:text-gray-300">{step.description}</p>
                  </div>
                </div>

                {/* Arrow to next step (on desktop) */}
                {index % 2 === 0 && index < steps.length - 1 && (
                  <div className="hidden md:block absolute -right-8 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-600">
                    <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </div>
                )}

                {/* Arrow down (for 1→2, 3→4 on desktop) */}
                {index === 1 && (
                  <div className="hidden md:block absolute left-1/2 -translate-x-1/2 -bottom-10 text-gray-400 dark:text-gray-600">
                    <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                    </svg>
                  </div>
                )}
              </>
            )
            
            return clickable && route ? (
              <Link key={step.key} to={route} className={cardClassName}>
                {cardContent}
              </Link>
            ) : (
              <div key={step.key} className={cardClassName}>
                {cardContent}
              </div>
            )
          })}
        </div>

        {/* Key Benefits */}
        <div className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                ✨ Why This Is Powerful
              </p>
              <ul className="text-xs text-gray-700 dark:text-gray-300 space-y-1">
                <li>• <strong>Keep your crypto:</strong> Retain custody of assets while accessing fiat</li>
                <li>• <strong>No capital gains tax:</strong> Not selling, so no taxable event</li>
                <li>• <strong>Maintain exposure:</strong> Your crypto can still appreciate in value</li>
                <li>• <strong>Real fiat money:</strong> Spend in the real world (mortgage, car, business)</li>
                <li>• <strong>Trustless guarantee:</strong> Smart contracts enforce terms automatically</li>
              </ul>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

